import * as React from 'react';
import { Popover, Transition } from '@headlessui/react';
import { BellIcon } from '@heroicons/react/24/outline';
import { useFetcher } from 'react-router';
import { twMerge } from 'tailwind-merge';

import {
  useMarkOrderNotificationsRead,
  useOrderNotificationTotalCount,
  useOrderNotificationUnreadCount,
  useOrderNotifications,
} from '~/hooks/useOrderNotifications';
import type { action } from '~/routes/_resources+/clear-notification';
import type { loader } from '~/routes/_resources+/fetch-notifications';
import type { OrderNotification } from '~/types/order';

import { NotificationContent } from './NotificationContent';

export function Notifications() {
  const rootNotifications = useOrderNotifications();
  const notificationUnreadCount = useOrderNotificationUnreadCount();
  const notificationTotalCount = useOrderNotificationTotalCount();
  const markNotificationsRead = useMarkOrderNotificationsRead();

  const [notifications, setNotifications] = React.useState(rootNotifications);
  const fetcher = useFetcher<typeof loader>();
  const clearNotificationsFetcher = useFetcher<typeof action>();

  const [prevFetcherData, setPrevFetcherData] = React.useState(fetcher.data);
  const [prevFetcherState, setPrevFetcherState] = React.useState(fetcher.state);
  if (fetcher.data !== prevFetcherData || fetcher.state !== prevFetcherState) {
    setPrevFetcherData(fetcher.data);
    setPrevFetcherState(fetcher.state);
    if (fetcher.data?.notifications && fetcher.state !== 'loading') {
      if (fetcher.data.total > fetcher.data.perPage * fetcher.data.currentPage) {
        setNotifications((prev) => [...prev, ...(fetcher.data?.notifications || [])]);
      }
    }
  }

  const [prevClearData, setPrevClearData] = React.useState(clearNotificationsFetcher.data);
  const [prevClearState, setPrevClearState] = React.useState(clearNotificationsFetcher.state);
  if (
    clearNotificationsFetcher.data !== prevClearData ||
    clearNotificationsFetcher.state !== prevClearState
  ) {
    setPrevClearData(clearNotificationsFetcher.data);
    setPrevClearState(clearNotificationsFetcher.state);
    const { notificationIds } = clearNotificationsFetcher.data || {};
    if (notificationIds?.length && clearNotificationsFetcher.state !== 'loading') {
      setNotifications((prev) => prev.filter((n) => !notificationIds.includes(n.id)));
    }
  }

  const [prevRootNotifications, setPrevRootNotifications] = React.useState(rootNotifications);
  if (rootNotifications !== prevRootNotifications) {
    setPrevRootNotifications(rootNotifications);
    setNotifications(rootNotifications);
  }

  function generateFetchMorePage() {
    if (clearNotificationsFetcher.data?.notificationIds.length && notifications.length <= 20) {
      return 0;
    }
    if (notifications.length <= 20) {
      return 0;
    }
    if (fetcher.data?.currentPage) {
      return fetcher.data.currentPage + 1;
    }
    return 1;
  }

  function fetchMore() {
    const page = generateFetchMorePage();
    fetcher.load(`/fetch-notifications?page=${page}`);
  }

  function handleOnScroll(e: React.UIEvent<HTMLDivElement, UIEvent>) {
    const el = e.currentTarget;
    const total = fetcher.data?.total || notificationTotalCount;
    const canFetch = notifications.length < total && fetcher.state !== 'loading';

    if (el.scrollTop + el.clientHeight === el.scrollHeight) {
      if (canFetch) {
        fetchMore();
      }

      const page = generateFetchMorePage();
      const perPage = fetcher.data?.perPage || 20;

      if (page === 0) {
        handleMarkNotificationsRead(notifications);
        return;
      }

      handleMarkNotificationsRead([...notifications].splice(perPage * page, perPage));
    }
  }

  function handleMarkNotificationsCleared(notificationIds: Array<string>) {
    const formData = new FormData();
    notificationIds.forEach((id) => formData.append('notificationIds', id));

    clearNotificationsFetcher.submit(formData, {
      method: 'POST',
      action: '/clear-notification',
      preventScrollReset: true,
    });
  }

  function handleMarkNotificationsRead(notifs: Array<OrderNotification>) {
    if (!notifications.length || fetcher.state === 'loading' || notificationUnreadCount === 0) {
      return;
    }

    const unreadNotifications = notifs.filter((n) => n.new).map((n) => n.id);
    if (!unreadNotifications.length) {
      return;
    }

    markNotificationsRead(unreadNotifications);
  }

  return (
    <Popover className="relative">
      <Popover.Button
        className="rounded-full bg-transparent p-1 focus:outline-none"
        name="notification-bell"
        onClick={() => handleMarkNotificationsRead(notifications)}
      >
        {({ open }) => (
          <div className="flex items-center">
            <span className="sr-only">View notifications</span>
            <BellIcon
              className={twMerge('h-6 w-6', open ? 'text-black' : 'text-gray-400')}
              fill={open ? 'black' : 'white'}
              aria-hidden="true"
            />
            {notificationUnreadCount > 0 ? (
              <span
                className={twMerge(
                  'ml-2 inline-flex h-6 w-6 items-center justify-center rounded-full p-0.5 text-center text-xs text-white',
                  open ? 'bg-black' : 'bg-gray-400',
                  notificationUnreadCount > 99 && 'text-[10px]'
                )}
              >
                {notificationUnreadCount > 99 ? '99+' : notificationUnreadCount}
              </span>
            ) : null}
          </div>
        )}
      </Popover.Button>
      <Transition
        as={React.Fragment}
        enterFrom="opacity-0 translate-y-1"
        enter="transition ease-out duration-200"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Popover.Panel
          aria-label="Notifications"
          className="notifications-open absolute right-0 z-10 mt-5 flex w-[384px] flex-col overflow-hidden rounded-md bg-white shadow"
          as="section"
        >
          <header className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-gray-50 px-3 py-2 text-sm leading-6 font-medium text-gray-900">
            <span>Notifications</span>
            <button
              className="text-sm text-[#9CA3AF]"
              name="clear-notifications"
              onClick={() => handleMarkNotificationsCleared(notifications.map((n) => n.id))}
            >
              Clear all
            </button>
          </header>
          <div onScroll={handleOnScroll} className="max-h-[520px] min-w-0 flex-1 overflow-y-auto">
            {notifications.length ? (
              <div className="mt-3 mb-0">
                {notifications.map((n) => (
                  <NotificationContent key={n.id} notification={n} />
                ))}
                {fetcher.state === 'loading' && (
                  <span className="block pb-3 text-center text-sm text-gray-500">Loading...</span>
                )}
              </div>
            ) : (
              <div className="mt-5 p-3">
                <span className="block text-center">You have no new notifications.</span>
              </div>
            )}
          </div>
        </Popover.Panel>
      </Transition>
    </Popover>
  );
}
